import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  LayoutChangeEvent,
  LayoutRectangle,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import useScale from '../../common/hooks/useScale';
import Constants from '../../utils/constants';
import AppIcon, { AppIcons } from '../app_icon';
import useGlobalStyles from '../../common/style_context';

export interface ToastContainerRef {
  show: (type: ToastType, message: string) => void;
  hideAll: () => void;
}

type Props = {
  duration?: number;
};

type ToastType = 'info' | 'danger' | 'warning' | 'success';

type ItemType = {
  id: number;
  type: ToastType;
  message: string;
  closing: boolean;
};

export const TvToastContainer = forwardRef<ToastContainerRef, Props>(
  (props, ref) => {
    const [toasts, setToasts] = useState<ItemType[]>([]);

    useImperativeHandle(
      ref,
      () => ({
        show: (type: ToastType, message: string) => {
          setToasts(p => {
            const id = (p.at(-1)?.id ?? 0) + 1;
            const n = { id, type, message, closing: false };
            if (p.length >= 5) return [...p.slice(1, 5), n];
            return [...p, n];
          });
        },
        hideAll: () => setToasts(p => p.map(e => ({ ...e, closing: true }))),
      }),
      [],
    );

    const onClosed = useCallback(
      (id: number) => setToasts(p => p.filter(t => t.id !== id)),
      [],
    );

    const [scale] = useScale();
    const style = useMemo(
      () =>
        StyleSheet.compose(styles.container, {
          paddingTop: Constants.tvSafeArea.top / scale,
          paddingHorizontal: Constants.tvSafeArea.horizontal / scale,
          paddingBottom: Constants.tvSafeArea.bottom / scale,
        }),
      [scale],
    );

    const items = useMemo(
      () =>
        toasts.map(e => (
          <ToastItem
            key={`${e.id}`}
            {...e}
            onClosed={onClosed}
            duration={props.duration ?? 3000}
          />
        )),
      [toasts, props.duration],
    );

    return <View style={style}>{items}</View>;
  },
);

type ItemProps = {
  duration: number;
  onClosed?: (id: number) => void;
} & ItemType;

const itemStyles = ['fr,ac,mb=12,p=16,br=16', 'fs=28,fw=400,ml=12'];

const ToastItem = memo<ItemProps>(
  ({ id, type, message, closing, duration, onClosed }) => {
    const [layout, setLayout] = useState<LayoutRectangle>();

    const [closingLocal, setClosing] = useState(false);
    const translate = useRef(new Animated.Value(0)).current;

    useEffect(() => {
      const t = setTimeout(() => setClosing(true), duration);
      return () => clearTimeout(t);
    }, []);

    useEffect(() => {
      Animated.timing(translate, {
        duration: 200,
        toValue: 1,
        useNativeDriver: true,
      }).start();
    }, []);

    useEffect(() => {
      if (!closing && !closingLocal) return;

      Animated.timing(translate, {
        duration: 200,
        toValue: 0,
        useNativeDriver: true,
      }).start(() => onClosed?.(id));
    }, [closing, closingLocal]);

    const onLayout = useCallback(
      (e: LayoutChangeEvent) => setLayout(e.nativeEvent.layout),
      [],
    );

    const [icon, color, bg] = useMemo(() => {
      switch (type) {
        case 'info':
          return [AppIcons.information_circle, '#0065D0', '#C9F0FF'];
        case 'warning':
          return [AppIcons.exclamation_triangle, '#A05E03', '#FFEFD7'];
        case 'danger':
          return [AppIcons.exclamation_circle, '#D3180C', '#FFE5E5'];
        case 'success':
          return [AppIcons.check_circle, '#198155', '#ECFCE5'];
      }
    }, [type]);

    const [container, textStyle] = useGlobalStyles(itemStyles);

    const style = useMemo(
      () =>
        StyleSheet.compose(container, {
          backgroundColor: bg,
          opacity: layout ? 1 : 0,
          transform: [
            {
              translateY: translate.interpolate({
                inputRange: [0, 1],
                outputRange: [-(layout?.height ?? 0), 0],
              }),
            },
          ],
        }),
      [layout],
    );
    const text = useMemo(
      () => StyleSheet.compose(textStyle, { color }),
      [color],
    );

    return (
      <Animated.View
        onLayout={onLayout}
        style={style}
        renderToHardwareTextureAndroid>
        <AppIcon name={icon} color={color} size={36} />
        <Text style={text}>{message}</Text>
      </Animated.View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'column-reverse',
    justifyContent: 'flex-end',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
